'use client';

import CardSkeleton from '@components/ascents/CardSkeleton';
import UserAvatar from '@components/users/UserAvatar';
import {
  ActionIcon,
  Anchor,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { FormEvent, useState } from 'react';
import PostMenu from './PostMenu';

interface Props {
  post: Post;
}

export default function PostCard({ post }: Props) {
  const router = useRouter();
  const { data: session } = useSession();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(post.content);
  const handleChange = (event) => {
    setValue(event.target.value);
  };
  const ref = useClickOutside(() => setEdit(false));

  const isCurrentUser = session?.user?._id === post?.user?._id;

  // handle edit post form
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    await fetch(`/api/posts/${post._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content: value,
      }),
    });
    setLoading(false);
    setEdit(false);
    router.refresh();
  }

  if (!post) {
    return <CardSkeleton />;
  }

  return (
    <Card shadow='md' py='lg'>
      <Card.Section withBorder inheritPadding py='xs'>
        <Group justify='space-between'>
          <Group>
            <UserAvatar user={post.user} />
            <Stack gap={0} justify='center'>
              <Anchor
                href={`/users/${post.user.username}`}
                fw='bold'
                m={0}
                lh={1.25}
                c='inherit'
              >
                {post.user.username}
              </Anchor>
              <Text span c='dimmed' size='xs' m={0} lh={1.25}>
                {new Date(post.createdAt).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </Text>
            </Stack>
          </Group>
          {isCurrentUser && <PostMenu post={post} setEdit={setEdit} />}
        </Group>
      </Card.Section>
      <Card.Section inheritPadding py='xs' ref={ref}>
        {edit ? (
          <TextInput
            size='md'
            disabled={!session}
            value={value}
            onChange={handleChange}
            rightSection={
              <ActionIcon
                loading={loading}
                color='brand'
                variant='light'
                disabled={!session}
                onClick={(e) => handleSubmit(e)}
              >
                <IconDeviceFloppy size={16} stroke={1.5} />
              </ActionIcon>
            }
          />
        ) : (
          post.content
        )}
      </Card.Section>
    </Card>
  );
}
